import {Container} from "../../Core/Container";
import {KitchenScreen} from "../../States/KitchenScreen";
import {Callback} from "../../Core/Reflection";
import {Once} from "../../Core/Signal";
import {Ease} from "../../Core/Animatable";
import {Sprite} from "../../Core/Sprite";
import {IPoint} from "../../Core/Geom";

export class CookingActivity extends Container {

    readonly kitchen: KitchenScreen;
    readonly steps: (new (kitchen: KitchenScreen) => CookingActivityStep)[];
    private prev?: CookingActivityStep;

    constructor(kitchen: KitchenScreen, steps: (new (kitchen: KitchenScreen) => CookingActivityStep)[]) {
        super();
        this.kitchen = kitchen;
        this.steps = steps;
    }

    play(onComplete: Callback) {
        let index = 0;
        const next = () => {
            if (index < this.steps.length) {
                const step = new (this.steps[index])(this.kitchen);
                this.addChild(step);
                index++;
                const p = this.prev;
                this.prev = step;
                step.play(Once(next), p);
            } else {
                onComplete();
            }
        }
        next();
    }
}

export abstract class CookingActivityStep extends Container {

    readonly kitchen: KitchenScreen;

    constructor(kitchen: KitchenScreen) {
        super();
        this.kitchen = kitchen;
    }

    abstract play(onComplete: Callback, prev?: CookingActivityStep): void;

    private moveItemsIn(side: -1 | 1, items: Container | Container[], cb?: Callback, delay = 0): void {
        if (!Array.isArray(items)) {
            items = [items];
        }
        for (const item of items) {
            item.moveXFrom(side < 0 ? this.kitchen.left - item.width * 0.5 : this.kitchen.right + item.width * 0.5, 0.6, Ease.backOut, {
                delay: delay,
                onComplete: item === items[items.length - 1] ? cb : undefined
            });
            delay += 0.2;
        }
    }

    private moveItemsOut(side: -1 | 1, items: Container | Container[], cb?: Callback, delay = 0): void {
        if (!Array.isArray(items)) {
            items = [items];
        }
        for (const item of items) {
            item.moveXTo(side < 0 ? this.kitchen.left - item.width * 0.5 : this.kitchen.right + item.width * 0.5, 0.3, Ease.sineIn, {
                delay: delay,
                onComplete: item === items[items.length - 1] ? cb : undefined
            });
            delay += 0.1;
        }
    }

    moveItemsFromLeft(items: Container | Container[], cb?: Callback, delay = 0) {
        this.moveItemsIn(-1, items, cb, delay);
    }

    moveItemsFromRight(items: Container | Container[], cb?: Callback, delay = 0) {
        this.moveItemsIn(1, items, cb, delay);
    }

    moveItemsToLeft(items: Container | Container[], cb?: Callback, delay = 0) {
        this.moveItemsOut(-1, items, cb, delay);
    }

    moveItemsToRight(items: Container | Container[], cb?: Callback, delay = 0) {
        this.moveItemsOut(1, items, cb, delay);
    }

    showArrow(arrow: Sprite, end: IPoint, cb?: Callback) {
        this.showArrowStep(arrow, {x: arrow.x, y: arrow.y}, end);
    }

    protected showArrowStep(arrow: Sprite, start: IPoint, end: IPoint) {
        arrow.visible = true;
        arrow.x = start.x;
        arrow.y = start.y;
        arrow.alpha = 0;
        arrow.alphaTo(1, 0.2, () => {
            arrow.tweenTo(1.6, Ease.sineInOut, {
                motionPath: {path: [{x: (end.x + arrow.x) * 0.5, y: Math.min(end.y, arrow.y) - arrow.height * 0.5}, {x: end.x, y: end.y}]},
                onComplete: () => arrow.alphaTo(0, 0.2, () => this.showArrowStep(arrow, start, end))
            });
        });
    }

    hideArrow(arrow: Sprite) {
        if (arrow.visible) {
            arrow.killTweens();
            arrow.alphaTo(0, 0.2, () => arrow.visible = false);
        }
    }
}